import React from "react";
import { Reveal, SectionHeading } from "@/components/common/Reveal";
import { CAPITAL_PATHWAYS, PILLARS, PROCESS_STAGES, IMAGES } from "@/data";
import { motion } from "framer-motion";
import { Layers, Cpu, Megaphone, Network, Factory, Users2 } from "lucide-react";

const PILLAR_ICONS = [Layers, Cpu, Megaphone, Network, Factory, Users2];

export function CapitalPathways() {
  return (
    <section id="capital" className="relative py-24 md:py-36 border-t border-white/5" data-testid="capital-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 items-start">
          <Reveal>
            <SectionHeading label="Capital Pathways" title="Capital designed around the business." />
            <p className="mt-6 text-base md:text-lg font-light text-zinc-400 leading-relaxed">
              Capital structure depends on the business stage, legal entity, financial position, eligibility, risk, use of funds and independent approval by the relevant provider.
            </p>
            <div className="mt-8 rounded-xl border border-[#0057FF]/30 bg-[#0057FF]/5 p-5">
              <p className="text-sm text-zinc-300 leading-relaxed">Submitting an application does not constitute an offer, sanction, term sheet, funding commitment, securities offering or loan approval.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid sm:grid-cols-2 gap-3">
              {CAPITAL_PATHWAYS.map((c, i) => (
                <motion.div key={c} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.05 }} className="flex items-center gap-3 rounded-lg border border-white/5 bg-[#0C0C10] px-4 py-3 card-hover hover:border-white/15">
                  <span className="font-mono text-[10px] text-[#0057FF]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm text-zinc-400">{c}</span>
                </motion.div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function GrowthEngine() {
  return (
    <section id="build-scale" className="relative py-24 md:py-36 border-t border-white/5 overflow-hidden" data-testid="growth-section">
      <div className="absolute right-0 top-0 w-[600px] h-[600px] glow-blue opacity-60" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <Reveal>
          <SectionHeading label="The FundAI Growth Engine" title="More than capital." className="max-w-3xl" />
          <p className="mt-6 max-w-2xl text-base md:text-lg font-light text-zinc-400 leading-relaxed">
            Capital alone does not create an enduring business. FundAI helps convert capital into execution.
          </p>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map((p, i) => {
            const Icon = PILLAR_ICONS[i];
            return (
              <Reveal key={p.title} delay={(i % 3) * 0.08}>
                <div className="card-hover h-full rounded-2xl border border-white/5 bg-[#0C0C10] p-7 hover:border-white/15" data-testid={`pillar-${i}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 bg-white/5"><Icon className="h-5 w-5 text-[#00D1FF]" /></div>
                  <h3 className="font-head text-xl font-medium mt-5">{p.title}</h3>
                  <ul className="mt-4 space-y-1.5">{p.items.map((it) => <li key={it} className="text-sm text-zinc-500">{it}</li>)}</ul>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 text-xs text-zinc-600 max-w-3xl leading-relaxed">Services, equity participation, commercial fees and responsibilities are determined only through separate written agreements.</p>
        </Reveal>
      </div>
    </section>
  );
}

export function Process() {
  return (
    <section className="relative py-24 md:py-36 border-t border-white/5" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionHeading label="Founder Process" title="From application to scale." className="max-w-3xl" />
        </Reveal>
        <div className="mt-14 relative">
          <div className="absolute left-[15px] md:left-1/2 top-0 bottom-0 w-px bg-white/10 hidden sm:block" />
          <div className="space-y-4">
            {PROCESS_STAGES.map((st, i) => (
              <motion.div
                key={st.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6 }}
                className={`relative sm:grid sm:grid-cols-2 sm:gap-12 items-center ${i % 2 ? "sm:[direction:rtl]" : ""}`}
                data-testid={`process-stage-${st.n}`}
              >
                <div className={`[direction:ltr] rounded-2xl border border-white/5 bg-[#0C0C10] p-6 card-hover hover:border-[#0057FF]/30 ${i % 2 ? "sm:text-right" : ""}`}>
                  <span className="font-mono text-3xl font-light text-zinc-700">{st.n}</span>
                  <h3 className="font-head text-xl font-medium mt-1">{st.title}</h3>
                  <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{st.desc}</p>
                </div>
                <div className="hidden sm:block" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
